/**
 * Centralized Error Codes for AgentNexus Frontend
 *
 * Error Code Format: NEXUS-XXXX
 * - 0001-0099: Authentication errors
 * - 0100-0199: Password reset errors
 * - 0200-0299: SSO errors
 * - 0300-0399: API communication errors
 * - 0400-0499: Validation errors
 * - 0500-0599: System errors
 */

export const ERROR_CODES = {
  // Authentication errors (0001-0099)
  AUTH_INVALID_CREDENTIALS: {
    code: 'NEXUS-0001',
    message: 'Invalid email or password. Please check your credentials and try again.',
    logLevel: 'warn'
  },
  AUTH_ACCOUNT_LOCKED: {
    code: 'NEXUS-0002',
    message: 'Your account has been temporarily locked. Please try again later or contact support.',
    logLevel: 'warn'
  },
  AUTH_ACCOUNT_NOT_FOUND: {
    code: 'NEXUS-0003',
    message: 'No account found with this email address.',
    logLevel: 'info'
  },
  AUTH_SESSION_EXPIRED: {
    code: 'NEXUS-0004',
    message: 'Your session has expired. Please sign in again.',
    logLevel: 'info'
  },

  // Password reset errors (0100-0199)
  RESET_TOKEN_INVALID: {
    code: 'NEXUS-0100',
    message: 'This password reset link is invalid or has expired. Please request a new one.',
    logLevel: 'warn'
  },
  RESET_TOKEN_EXPIRED: {
    code: 'NEXUS-0101',
    message: 'This password reset link has expired. Please request a new one.',
    logLevel: 'info'
  },
  RESET_EMAIL_SEND_FAILED: {
    code: 'NEXUS-0102',
    message: 'Unable to send password reset email. Please try again later.',
    logLevel: 'error'
  },
  RESET_PASSWORD_MISMATCH: {
    code: 'NEXUS-0103',
    message: 'Passwords do not match. Please ensure both passwords are identical.',
    logLevel: 'info'
  },
  RESET_PASSWORD_WEAK: {
    code: 'NEXUS-0104',
    message: 'Password does not meet security requirements.',
    logLevel: 'info'
  },
  RESET_ENDPOINT_NOT_FOUND: {
    code: 'NEXUS-0105',
    message: 'Password reset service is temporarily unavailable. Please try again later.',
    logLevel: 'error'
  },

  // SSO errors (0200-0299)
  SSO_TOKEN_INVALID: {
    code: 'NEXUS-0200',
    message: 'Single sign-on authentication failed. Please sign in again.',
    logLevel: 'warn'
  },
  SSO_TOKEN_EXPIRED: {
    code: 'NEXUS-0201',
    message: 'Your SSO session has expired. Please sign in again.',
    logLevel: 'info'
  },
  SSO_VALIDATION_FAILED: {
    code: 'NEXUS-0202',
    message: 'Unable to validate your credentials. Please try signing in again.',
    logLevel: 'error'
  },

  // API communication errors (0300-0399)
  API_NETWORK_ERROR: {
    code: 'NEXUS-0300',
    message: 'Unable to connect to the server. Please check your internet connection.',
    logLevel: 'error'
  },
  API_TIMEOUT: {
    code: 'NEXUS-0301',
    message: 'The request timed out. Please try again.',
    logLevel: 'warn'
  },
  API_NOT_FOUND: {
    code: 'NEXUS-0302',
    message: 'The requested service is not available. Please try again later.',
    logLevel: 'error'
  },
  API_SERVER_ERROR: {
    code: 'NEXUS-0303',
    message: 'An unexpected server error occurred. Please try again later.',
    logLevel: 'error'
  },
  API_UNAUTHORIZED: {
    code: 'NEXUS-0304',
    message: 'You are not authorized to perform this action. Please sign in again.',
    logLevel: 'warn'
  },

  // Validation errors (0400-0499)
  VALIDATION_EMAIL_INVALID: {
    code: 'NEXUS-0400',
    message: 'Please enter a valid email address.',
    logLevel: 'info'
  },
  VALIDATION_REQUIRED_FIELD: {
    code: 'NEXUS-0401',
    message: 'Please fill in all required fields.',
    logLevel: 'info'
  },

  // System errors (0500-0599)
  SYSTEM_UNEXPECTED: {
    code: 'NEXUS-0500',
    message: 'An unexpected error occurred. Please try again or contact support.',
    logLevel: 'error'
  },
  SYSTEM_MAINTENANCE: {
    code: 'NEXUS-0501',
    message: 'The system is currently under maintenance. Please try again later.',
    logLevel: 'info'
  }
};

/**
 * Log levels for observability
 */
const LOG_LEVELS = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3
};

/**
 * Get environment info for logging
 */
const getEnvironmentInfo = () => {
  const apiUrl = process.env.REACT_APP_API_URL || '';
  const isProduction = apiUrl.includes('azurecontainerapps.io') || !apiUrl.includes('localhost');
  return {
    environment: isProduction ? 'production' : 'development',
    apiUrl: apiUrl,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    timestamp: new Date().toISOString(),
    url: typeof window !== 'undefined' ? window.location.href : 'unknown'
  };
};

/**
 * Send log to observability system
 * In production: logs are captured by Azure Application Insights via console methods
 * In development: logs appear in browser console
 */
const sendLog = (level, code, message, details = {}) => {
  const envInfo = getEnvironmentInfo();
  const logEntry = {
    code,
    message,
    level,
    ...envInfo,
    details
  };

  // Format log entry for structured logging
  const structuredLog = JSON.stringify(logEntry);

  // Use appropriate console method based on level
  // Azure Application Insights captures these via browser SDK
  switch (level) {
    case 'error':
      console.error(`[${code}] ${message}`, logEntry);
      break;
    case 'warn':
      console.warn(`[${code}] ${message}`, logEntry);
      break;
    case 'info':
      console.info(`[${code}] ${message}`, logEntry);
      break;
    default:
      console.log(`[${code}] ${message}`, logEntry);
  }

  // In production, also send to a logging endpoint if available
  if (envInfo.environment === 'production' && process.env.REACT_APP_LOG_ENDPOINT) {
    fetch(process.env.REACT_APP_LOG_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: structuredLog
    }).catch(() => {
      // Silently fail - don't cause additional errors from logging
    });
  }
};

/**
 * Log an error with full context
 * @param {Object} errorDef - Error definition from ERROR_CODES
 * @param {Object} details - Additional details for logging
 */
export const logError = (errorDef, details = {}) => {
  sendLog(errorDef.logLevel || 'error', errorDef.code, errorDef.message, details);
};

/**
 * Map HTTP status codes to error definitions
 * @param {number} status - HTTP status code
 * @param {string} context - Context of the error (e.g., 'password_reset', 'login')
 * @returns {Object} Error definition
 */
export const mapHttpStatusToError = (status, context = 'general') => {
  // Context-specific mappings
  if (context === 'password_reset') {
    switch (status) {
      case 400:
        return ERROR_CODES.RESET_TOKEN_INVALID;
      case 404:
        return ERROR_CODES.RESET_ENDPOINT_NOT_FOUND;
      case 410:
        return ERROR_CODES.RESET_TOKEN_EXPIRED;
    }
  }

  if (context === 'login') {
    switch (status) {
      case 401:
        return ERROR_CODES.AUTH_INVALID_CREDENTIALS;
      case 403:
        return ERROR_CODES.AUTH_ACCOUNT_LOCKED;
      case 404:
        return ERROR_CODES.AUTH_ACCOUNT_NOT_FOUND;
    }
  }

  // Generic status mappings
  switch (status) {
    case 401:
      return ERROR_CODES.API_UNAUTHORIZED;
    case 404:
      return ERROR_CODES.API_NOT_FOUND;
    case 500:
    case 502:
    case 503:
      return ERROR_CODES.API_SERVER_ERROR;
    case 504:
      return ERROR_CODES.API_TIMEOUT;
    default:
      return ERROR_CODES.SYSTEM_UNEXPECTED;
  }
};

/**
 * Handle API errors with consistent logging and user-friendly messages
 * @param {Error|Response} error - The error or response object
 * @param {string} context - Context of the error
 * @param {Object} additionalInfo - Extra info for logging
 * @returns {Object} { code, message } for display
 */
export const handleApiError = async (error, context = 'general', additionalInfo = {}) => {
  let errorDef;
  let serverMessage = null;
  let serverCode = null;

  if (error instanceof Response) {
    // HTTP response error
    try {
      const data = await error.json();
      serverMessage = data.detail || data.message;
      serverCode = data.error_code;
    } catch {
      // Could not parse response body
    }

    // If server provided an error code, try to match it
    if (serverCode && ERROR_CODES[serverCode]) {
      errorDef = ERROR_CODES[serverCode];
    } else {
      errorDef = mapHttpStatusToError(error.status, context);
    }
  } else if (error instanceof TypeError && error.message.includes('fetch')) {
    // Network error
    errorDef = ERROR_CODES.API_NETWORK_ERROR;
  } else {
    // Unknown error
    errorDef = ERROR_CODES.SYSTEM_UNEXPECTED;
  }

  // Log the error with full context
  logError(errorDef, {
    context,
    serverMessage,
    serverCode,
    originalError: error instanceof Error ? error.message : String(error),
    ...additionalInfo
  });

  return {
    code: errorDef.code,
    message: errorDef.message,
    displayMessage: `${errorDef.message} (${errorDef.code})`
  };
};

/**
 * Get API base URL with correct path handling
 * Auth endpoints are at root level (no /api prefix)
 * @param {boolean} isAuthEndpoint - Whether this is an auth endpoint
 * @returns {string} The correct base URL
 */
export const getApiBaseUrl = (isAuthEndpoint = false) => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3050/api';

  if (isAuthEndpoint) {
    // Auth endpoints are at root level, remove /api suffix
    return apiUrl.replace(/\/api\/?$/, '');
  }

  return apiUrl;
};
